/**
 * Button-style extraction from template section CSS (Part 3).
 *
 * `detectTemplateButtonEffect` is the ONLY thing the template-load path calls: it
 * sniffs the bubbly-hover effect and nothing else. It never produces root-rule
 * button styles, so it can never trigger a site-wide button strip/override — the
 * root cause of "buttons re-styled site-wide without the user editing buttons".
 *
 * `extractButtonStyles` (the old first-section-colors-become-site-wide extractor)
 * is RETAINED ONLY as a migration helper: schemaVersion-5 migration recomputes
 * what it WOULD have produced to decide whether a saved project's buttonStyles
 * were silently auto-extracted (reset) or explicitly user-edited (kept).
 *
 * Pure leaf module — imported by section-manager (template load) and save-load
 * (migration) with no cycle.
 */

function matchCSSDeclaration(ruleChunk, propertyName) {
  if (!ruleChunk || !propertyName) return '';
  const escaped = propertyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = ruleChunk.match(new RegExp(`(?:^|[;{])\\s*${escaped}\\s*:\\s*([^;]+)`, 'i'));
  return match ? match[1].trim() : '';
}

/**
 * Migration-only. Scan section instance CSS for the first non-pseudo
 * .cs-button-solid rule and pull border-radius/padding/background-color/color.
 * Returns a buttonStyles object (with optional hoverEffect) or null.
 */
export function extractButtonStyles(sections) {
  for (const inst of sections || []) {
    const css = inst && inst.css;
    if (!css || !css.includes('cs-button-solid')) continue;

    const chunks = css.split('}');
    for (const chunk of chunks) {
      if (!chunk.includes('cs-button-solid')) continue;
      if (chunk.includes(':before') || chunk.includes(':after') || chunk.includes(':hover'))
        continue;

      const br = matchCSSDeclaration(chunk, 'border-radius');
      const pd = matchCSSDeclaration(chunk, 'padding');
      const bg = matchCSSDeclaration(chunk, 'background-color');
      const cl = matchCSSDeclaration(chunk, 'color');

      const styles = {};
      if (br) styles.borderRadius = br;
      if (pd) styles.padding = pd;
      if (bg) styles.backgroundColor = bg;
      if (cl) styles.textColor = cl;

      if (Object.keys(styles).length > 0) {
        for (const other of sections) {
          if (other && other.html && other.html.includes('bubbly-button')) {
            styles.hoverEffect = 'bubble';
            break;
          }
        }
        return styles;
      }
    }
  }

  for (const inst of sections || []) {
    if (inst && inst.html && inst.html.includes('bubbly-button')) {
      return { hoverEffect: 'bubble' };
    }
  }
  return null;
}

/**
 * The template-load extractor. Detects the bubbly-hover button effect only —
 * never any root-rule styling. Returns { hoverEffect:'bubble', origin:'auto' }
 * or null. `origin:'auto'` marks the styles as template-derived (not user
 * intent) so the export/preview strip logic (hasButtonRootRules) leaves the
 * template's own button rules intact.
 */
export function detectTemplateButtonEffect(sections) {
  for (const inst of sections || []) {
    if (inst && inst.html && inst.html.includes('bubbly-button')) {
      return { hoverEffect: 'bubble', origin: 'auto' };
    }
  }
  return null;
}
